import { CloseOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Space, Table, Typography } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useCustomPricing,
  useCustomPricingCreate,
  useCustomPricingRemove,
  useCustomPricingUpdate,
  useModes,
} from '../../hooks/apiHooks';
import { Carrier } from '../../models/Carrier';
import { CustomPricing, CustomPricingInput } from '../../models/CustomPricing';
import Container from '../common/Container';
import PricingModal from '../modals/PricingModal';

interface IProps {
  carrier: Carrier;
  onHeaderActions?: (actions: React.ReactNode) => void;
}

const formatCurrency = (value: number) =>
  `$${value.toLocaleString('en-CA', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })}`;

const CustomPricingList: React.FC<IProps> = ({ carrier, onHeaderActions }) => {
  const { configs, isLoading } = useCustomPricing(carrier?.id);
  const { modes } = useModes(carrier?.id);

  const createMutation = useCustomPricingCreate(carrier.id);
  const updateMutation = useCustomPricingUpdate(carrier.id);
  const removeMutation = useCustomPricingRemove(carrier.id);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editableConfig, setEditableConfig] = useState<
    CustomPricing | undefined
  >();
  const [deletableConfig, setDeletableConfig] = useState<
    CustomPricing | undefined
  >();

  const modeNameById = useMemo(
    () => Object.fromEntries(modes.map((m) => [m.id, m.name])),
    [modes]
  );

  const onAdd = useCallback(
    (values: CustomPricingInput) => {
      createMutation.mutate(values);
    },
    [createMutation]
  );

  const onUpdate = useCallback(
    (values: CustomPricingInput) => {
      if (!editableConfig) return;
      updateMutation.mutate({ body: values, id: editableConfig.id });
    },
    [editableConfig, updateMutation]
  );

  const onEdit = useCallback((config: CustomPricing) => {
    setEditableConfig(config);
    setShowEditModal(true);
  }, []);

  const onDelete = useCallback(() => {
    if (deletableConfig) {
      removeMutation.mutate(deletableConfig.id);
    }
  }, [deletableConfig, removeMutation]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createMutation.isSuccess]);

  useEffect(() => {
    if (updateMutation.isSuccess) {
      setShowEditModal(false);
      setEditableConfig(undefined);
    }
  }, [updateMutation.isSuccess]);

  useEffect(() => {
    if (removeMutation.isSuccess) {
      setDeletableConfig(undefined);
    }
  }, [removeMutation.isSuccess]);

  const headerActions = useMemo(
    () => (
      <Button
        ghost
        icon={<PlusOutlined />}
        style={{ width: 170 }}
        type="primary"
        onClick={() => setShowAddModal(true)}
      >
        Add Custom Pricing
      </Button>
    ),
    []
  );

  const headerActionsRef = useRef(headerActions);
  headerActionsRef.current = headerActions;

  useEffect(() => {
    if (onHeaderActions) {
      onHeaderActions(headerActionsRef.current);
    }
  }, [onHeaderActions]);

  const columns = useMemo(
    () => [
      {
        dataIndex: 'modeId',
        key: 'modeId',
        render: (modeId: number) => modeNameById[modeId] ?? modeId,
        title: 'Mode',
      },
      {
        dataIndex: 'hookUpCharge',
        key: 'hookUpCharge',
        render: (v: number) => formatCurrency(v),
        title: 'Hook-up Charge',
      },
      {
        dataIndex: 'hourlyRate',
        key: 'hourlyRate',
        render: (v: number) => `${formatCurrency(v)}/hr`,
        title: 'Hourly Rate',
      },
      {
        dataIndex: 'speed',
        key: 'speed',
        render: (v: number) => `${v} km/h`,
        title: 'Speed',
      },
      {
        dataIndex: 'minimumFee',
        key: 'minimumFee',
        render: (v: number) => formatCurrency(v),
        title: 'Minimum Fee',
      },
      {
        dataIndex: 'allottedWaitTime',
        key: 'allottedWaitTime',
        render: (v: number) => (v === 0 ? '' : `${v} hr${v !== 1 ? 's' : ''}`),
        title: 'Allotted Wait Time',
      },
      {
        key: 'actions',
        render: (_: unknown, record: CustomPricing) => (
          <Space>
            <Button
              icon={<EditOutlined />}
              shape="circle"
              size="small"
              onClick={() => onEdit(record)}
            />
            <Popconfirm
              okButtonProps={{ loading: removeMutation.isLoading }}
              open={deletableConfig?.id === record.id}
              title={
                <Typography.Text>
                  Delete pricing for{' '}
                  <strong>
                    {modeNameById[record.modeId] ?? record.modeId}
                  </strong>
                  ?
                </Typography.Text>
              }
              onCancel={() => setDeletableConfig(undefined)}
              onConfirm={onDelete}
            >
              <Button
                danger
                icon={<CloseOutlined />}
                shape="circle"
                size="small"
                onClick={() => setDeletableConfig(record)}
              />
            </Popconfirm>
          </Space>
        ),
        title: 'Actions',
        width: 100,
      },
    ],
    [deletableConfig, modeNameById, onDelete, onEdit, removeMutation.isLoading]
  );

  return (
    <Container id="custom-pricing">
      <Table
        columns={columns}
        dataSource={configs}
        loading={isLoading}
        pagination={false}
        rowKey="id"
      />

      {showAddModal && (
        <PricingModal
          carrierId={carrier.id}
          configs={configs}
          error={createMutation.error}
          modes={modes}
          submitting={createMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAdd}
        />
      )}

      {showEditModal && editableConfig && (
        <PricingModal
          carrierId={carrier.id}
          configs={configs}
          error={updateMutation.error}
          modes={modes}
          selectedConfig={editableConfig}
          submitting={updateMutation.isLoading}
          onClose={() => {
            setShowEditModal(false);
            setEditableConfig(undefined);
          }}
          onSubmit={onUpdate}
        />
      )}
    </Container>
  );
};

export default CustomPricingList;
